import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import {
  ContactSubmission,
  type Attribution,
  type PayoutWallet,
  type VolumeTier,
  type Timeline,
  type ReferralSource,
} from "@vb/rgs-contract";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

type WalletChain = PayoutWallet["chain"];
type WalletCurrency = PayoutWallet["currency"];

type FormState = {
  operator: string;
  name: string;
  email: string;
  country: string;
  volumeTier: VolumeTier;
  message: string;
  timeline: Timeline | "";
  referral: ReferralSource | "";
  walletChain: WalletChain | "";
  walletCurrency: WalletCurrency | "";
  walletAddress: string;
  website: string; // honeypot
};

const EMPTY: FormState = {
  operator: "",
  name: "",
  email: "",
  country: "",
  volumeTier: "5k-50k",
  message: "",
  timeline: "",
  referral: "",
  walletChain: "",
  walletCurrency: "",
  walletAddress: "",
  website: "",
};

const TIER_OPTIONS: VolumeTier[] = ["0-5k", "5k-50k", "50k-500k", "500k+"];
const TIMELINE_OPTIONS: Timeline[] = ["exploring", "next-quarter", "this-quarter", "urgent"];
const REFERRAL_OPTIONS: ReferralSource[] = ["search", "referral", "event", "press", "other"];
const WALLET_CHAINS: WalletChain[] = ["solana", "ethereum", "polygon"];
const WALLET_CURRENCIES: WalletCurrency[] = ["USDT", "USDC"];

/** Top 35 iGaming jurisdictions (regulated + grey-market hubs that operators
 *  ask us about most often) plus an "Other" escape hatch. Order is curated:
 *  EU regulated first, then North America, LatAm, APAC, MEA. */
const COUNTRY_OPTIONS: string[] = [
  "Malta",
  "Gibraltar",
  "Isle of Man",
  "Curaçao",
  "United Kingdom",
  "Estonia",
  "Sweden",
  "Germany",
  "Netherlands",
  "Spain",
  "Italy",
  "France",
  "Cyprus",
  "Israel",
  "United States",
  "Canada",
  "Brazil",
  "Mexico",
  "Argentina",
  "Peru",
  "Chile",
  "Colombia",
  "India",
  "Philippines",
  "Singapore",
  "Hong Kong",
  "Australia",
  "New Zealand",
  "South Africa",
  "Nigeria",
  "United Arab Emirates",
  "Turkey",
  "Switzerland",
  "Ireland",
  "Denmark",
  "Finland",
  "Norway",
  "Other",
];

type Status = "idle" | "submitting" | "success" | "error" | "server-error";

export function Contact() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const onChange =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((f) => ({ ...f, [key]: e.target.value as FormState[K] }));
    };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Build the payload up before validation so we can graft on the optional
    // wallet + UTM blocks without leaking empty strings into the schema.
    const wallet =
      form.walletChain && form.walletCurrency && form.walletAddress.trim().length >= 20
        ? {
            chain: form.walletChain as WalletChain,
            currency: form.walletCurrency as WalletCurrency,
            address: form.walletAddress.trim(),
          }
        : undefined;

    // Pull UTM/referrer attribution from the lib that Agent 7 owns. Dynamic
    // import + try/catch so a not-yet-built lib doesn't take down the form.
    let utm: Attribution | undefined;
    try {
      const mod = (await import("../lib/utm.js")) as {
        getAttribution?: () => Attribution;
      };
      utm = mod.getAttribution?.();
    } catch {
      utm = undefined;
    }

    const payload = {
      operator: form.operator,
      name: form.name,
      email: form.email,
      country: form.country,
      volumeTier: form.volumeTier,
      message: form.message,
      timeline: form.timeline || undefined,
      referral: form.referral || undefined,
      website: form.website,
      ...(wallet ? { payoutWallet: wallet } : {}),
      ...(utm ? { utm } : {}),
    };

    const parsed = ContactSubmission.safeParse(payload);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setError(first ? `${first.path.join(".")}: ${first.message}` : "Invalid submission");
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setStatus("error");
          setError("Too many submissions — try again in a few minutes.");
          return;
        }
        if (res.status >= 500) {
          setStatus("server-error");
          return;
        }
        setStatus("error");
        setError(`Submission rejected (${res.status}).`);
        return;
      }
      setStatus("success");
      // Best-effort analytics — don't let a missing/broken lib break the UX.
      try {
        const mod = (await import("../lib/analytics.js")) as {
          track?: (event: string, props: Record<string, unknown>) => void;
        };
        mod.track?.("contact_submit_success", {
          volume_tier: parsed.data.volumeTier,
          timeline: parsed.data.timeline,
          referral: parsed.data.referral,
        });
      } catch {
        /* analytics lib not built yet — ignore */
      }
    } catch (err) {
      // Network / DNS / offline — also surface mailto fallback.
      setStatus("server-error");
      setError(err instanceof Error ? err.message : "Network error");
    }
  };

  if (status === "success") {
    return (
      <>
        <Seo title="Thanks · Vault Breaker Studios" path="/contact" />
        <Section style={{ paddingTop: 120, paddingBottom: 120, maxWidth: 720 }}>
          <Badge kind="fair" style={{ marginBottom: 18 }}>
            RECEIVED
          </Badge>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px, 5vw, 56px)",
              lineHeight: 1.05,
              margin: 0,
              textTransform: "uppercase",
            }}
          >
            Thanks. A human replies within one business day.
          </h1>
          <p style={{ fontSize: 16, color: "var(--fg-2)", marginTop: 16 }}>
            If it's urgent, you can also drop a line directly to{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
              hello@vaultbreaker.dev
            </a>
            .
          </p>
          <div style={{ marginTop: 24 }}>
            <Link to="/book" style={{ textDecoration: "none" }}>
              <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
                Want a faster reply? Book a 30-min intro →
              </Button>
            </Link>
          </div>
        </Section>
      </>
    );
  }

  if (status === "server-error") {
    return (
      <>
        <Seo title="Something broke · Vault Breaker Studios" path="/contact" />
        <Section style={{ paddingTop: 120, paddingBottom: 120, maxWidth: 720 }}>
          <Badge kind="bust" style={{ marginBottom: 18 }}>
            SERVER HICCUP
          </Badge>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(32px, 4.5vw, 48px)",
              lineHeight: 1.05,
              margin: 0,
              textTransform: "uppercase",
            }}
          >
            We couldn't take that submission.
          </h1>
          <p style={{ fontSize: 16, color: "var(--fg-2)", marginTop: 16 }}>
            The form server is being a diva. Send us your message directly and we'll pick it up
            from there — your text is already pre-filled.
          </p>
          <Card style={{ padding: 22, marginTop: 24, display: "grid", gap: 14 }}>
            <div style={{ fontSize: 14, color: "var(--fg-2)" }}>
              <strong style={{ color: "var(--fg-1)" }}>Email us directly</strong>
              <br />
              <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
                hello@vaultbreaker.dev
              </a>
            </div>
            <a
              href={buildMailto(form)}
              style={{ textDecoration: "none", justifySelf: "start" }}
            >
              <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
                Open email with my message ↗
              </Button>
            </a>
            {error ? (
              <span
                style={{
                  fontSize: 12,
                  color: "var(--fg-3)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {error}
              </span>
            ) : null}
          </Card>
          <div style={{ marginTop: 18 }}>
            <Button
              variant="ghost"
              onClick={() => setStatus("idle")}
              style={{ padding: "12px 18px", fontSize: 14 }}
            >
              ← Try the form again
            </Button>
          </div>
        </Section>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Contact sales · Vault Breaker Studios"
        description="Get in touch with Vault Breaker Studios. Operator enquiries, integration questions, press."
        path="/contact"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          CONTACT SALES
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Let's talk integration.
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          We reply within one business day. If it's urgent,{" "}
          <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
            hello@vaultbreaker.dev
          </a>{" "}
          hits the same queue.
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 760 }}>
        <Card style={{ padding: 28 }}>
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
            <Row>
              <Field label="Operator / brand" required>
                <input
                  value={form.operator}
                  onChange={onChange("operator")}
                  className="vb-text-input"
                  required
                  minLength={2}
                />
              </Field>
              <Field label="Your name" required>
                <input
                  value={form.name}
                  onChange={onChange("name")}
                  className="vb-text-input"
                  required
                  minLength={2}
                />
              </Field>
            </Row>

            <Row>
              <Field label="Work email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  className="vb-text-input"
                  required
                />
              </Field>
              <Field label="Country" required>
                <select
                  value={form.country}
                  onChange={onChange("country")}
                  className="vb-text-input"
                  required
                >
                  <option value="" disabled>
                    Select a jurisdiction…
                  </option>
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>

            <Row>
              <Field label="Player volume (MAU)" required>
                <select
                  value={form.volumeTier}
                  onChange={onChange("volumeTier")}
                  className="vb-text-input"
                >
                  {TIER_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Timeline">
                <select
                  value={form.timeline}
                  onChange={onChange("timeline")}
                  className="vb-text-input"
                >
                  <option value="">—</option>
                  {TIMELINE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t.replace("-", " ")}
                    </option>
                  ))}
                </select>
              </Field>
            </Row>

            <Field label="How did you hear about us?">
              <select
                value={form.referral}
                onChange={onChange("referral")}
                className="vb-text-input"
              >
                <option value="">—</option>
                {REFERRAL_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Message" required>
              <textarea
                value={form.message}
                onChange={onChange("message")}
                className="vb-text-input"
                rows={5}
                required
                minLength={10}
                placeholder="Tell us about your lobby, current aggregators, and what you'd like from a pilot."
                style={{ resize: "vertical", minHeight: 120 }}
              />
            </Field>

            <details
              style={{
                border: "1px solid var(--line)",
                borderRadius: 10,
                padding: "10px 14px",
                background: "rgba(0,0,0,0.18)",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--fg-2)",
                  fontFamily: "var(--font-mono)",
                  letterSpacing: 0.4,
                }}
              >
                Optional: payout wallet for fast onboarding
              </summary>
              <div style={{ display: "grid", gap: 14, marginTop: 14 }}>
                <Row>
                  <Field label="Chain">
                    <select
                      value={form.walletChain}
                      onChange={onChange("walletChain")}
                      className="vb-text-input"
                    >
                      <option value="">—</option>
                      {WALLET_CHAINS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Currency">
                    <select
                      value={form.walletCurrency}
                      onChange={onChange("walletCurrency")}
                      className="vb-text-input"
                    >
                      <option value="">—</option>
                      {WALLET_CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                </Row>
                <Field label="Address">
                  <input
                    value={form.walletAddress}
                    onChange={onChange("walletAddress")}
                    className="vb-text-input"
                    placeholder="Wallet address (you can verify ownership later)"
                  />
                </Field>
                <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
                  We never debit your wallet — this just lets us prep payout rails before the
                  pilot kicks off.
                </span>
              </div>
            </details>

            {/* Honeypot — hidden for humans, filled by bots */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={onChange("website")}
              tabIndex={-1}
              autoComplete="off"
              style={{
                position: "absolute",
                left: "-10000px",
                width: 1,
                height: 1,
                opacity: 0,
              }}
              aria-hidden
            />

            {error ? (
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px solid rgba(255,59,71,0.35)",
                  background: "var(--red-wash)",
                  color: "var(--red)",
                  fontSize: 13,
                  borderRadius: 10,
                }}
              >
                {error}
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
                By submitting you agree to our press terms. We don't sell your contact info.
              </span>
              <Button
                variant="gold"
                type="submit"
                disabled={status === "submitting"}
                style={{ padding: "14px 22px", fontSize: 15 }}
              >
                {status === "submitting" ? "SENDING…" : "CONTACT SALES"}
              </Button>
            </div>
          </form>
        </Card>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 760 }}>
        <SectionHeading eyebrow="Other channels" title="For everything else" />
        <Card style={{ display: "grid", gap: 10, padding: 22 }}>
          <span className="label" style={{ color: "var(--fg-2)" }}>
            Sales · Press · Support (operators)
          </span>
          <a
            href="mailto:hello@vaultbreaker.dev"
            style={{ color: "var(--fg-1)", fontSize: 18, fontWeight: 600 }}
          >
            hello@vaultbreaker.dev
          </a>
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
            We respond within 1 business day.
          </span>
        </Card>
      </Section>
    </>
  );
}

/** Builds a `mailto:` URL from the user's typed form so the 5xx fallback CTA
 *  drops them into their email client with the message body pre-filled. The
 *  operator name is URL-encoded into the subject so we can still triage. */
function buildMailto(form: FormState): string {
  const operator = form.operator.trim() || "your studio";
  const subject = `Inquiry from ${operator}`;
  const bodyLines = [
    form.message.trim() || "(your message)",
    "",
    "—",
    `Operator: ${form.operator}`,
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    `Country: ${form.country}`,
    `Volume: ${form.volumeTier}`,
    form.timeline ? `Timeline: ${form.timeline}` : "",
    form.referral ? `Referral: ${form.referral}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:hello@vaultbreaker.dev?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg-2)" }}>
        {label}
        {required ? <span style={{ color: "var(--red)" }}> *</span> : null}
      </span>
      {children}
    </label>
  );
}
